import { useMemo, useState } from 'react';
import {
  Stack, Title, Card, Group, Button, Table, Text, Checkbox, Radio, ScrollArea, Skeleton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconReportAnalytics } from '@tabler/icons-react';
import { LocationSelect } from '../components/LocationSelect.tsx';
import { api } from '../api/client.ts';

/**
 * Government-form report page. Mirrors the legacy /gov-form workflow:
 *   - Pick a from/to date range
 *   - Pick a location
 *   - Get the list of visitors with travel touching that location in that range
 *   - Select rows
 *   - "Copy to clipboard" produces a pipe-delimited blob for pasting into
 *     the gov filing form
 *
 * The selected rows render as a tristate Mantine table.
 */
interface ReportRow {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  gender?: string | null;
  country?: string | null;
  nationality?: string | null;
  address?: string | null;
  dateOfBirth?: string | null;
  passportNumber?: string | null;
  passportValidTill?: string | null;
  phones?: string | null;
  email?: string | null;
  lastUpdated?: string | null;
  [k: string]: unknown;
}

/**
 * Match the legacy formatCopyLine exactly:
 *   firstName | lastName | gender | country | nationality | address |
 *   dateOfBirth(dd/MM/yyyy) | passportNumber | passportValidTill(dd/MM/yyyy) | phones
 *
 * Source: src/main/webapp/app/gov-form/gov-form.component.ts#formatCopyLine.
 */
function formatRow(r: ReportRow): string {
  return [
    r.firstName ?? '',
    r.lastName ?? '',
    r.gender ?? '',
    r.country ?? '',
    r.nationality ?? '',
    r.address ?? '',
    formatDmy(r.dateOfBirth ?? null),
    r.passportNumber ?? '',
    formatDmy(r.passportValidTill ?? null),
    r.phones ?? '',
  ].join('|');
}

function formatDmy(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function ymd(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function GovFormPage() {
  const [from, setFrom] = useState<Date | null>(firstOfMonth());
  const [to, setTo] = useState<Date | null>(lastOfMonth());
  const [locationId, setLocationId] = useState<number | null>(null);
  const [onlyChanges, setOnlyChanges] = useState(false);

  const params = useMemo(() => {
    if (!from || !to || locationId == null) return null;
    return { from: ymd(from)!, to: ymd(to)!, location: locationId, onlyChanges };
  }, [from, to, locationId, onlyChanges]);

  const query = useQuery({
    queryKey: ['gov-form', params],
    queryFn: async (): Promise<ReportRow[]> => {
      const res = await api.get('/api/visitors-report', { params });
      if (res.status !== 200) throw new Error(`status ${res.status}`);
      return res.data as ReportRow[];
    },
    enabled: params != null,
  });

  const rows = query.data ?? [];
  const [selectedId, setSelectedId] = useState<number | null>(null);

  async function copy() {
    const chosen = rows.find(r => r.id === selectedId);
    if (!chosen) {
      notifications.show({ message: 'Pick a row first', color: 'yellow' });
      return;
    }
    try {
      await navigator.clipboard.writeText(formatRow(chosen));
      notifications.show({ message: 'Row copied', color: 'green' });
    } catch {
      notifications.show({ message: 'Clipboard access denied', color: 'red' });
    }
  }

  return (
    <Stack gap="md">
      <Title order={2}>Government Form</Title>

      <Card withBorder padding="md" radius="md">
        <Group align="flex-end" gap="md" wrap="wrap">
          <DatePickerInput
            label="From"
            value={from}
            onChange={d => setFrom(d as Date | null)}
            valueFormat="DD/MM/YYYY"
            w={160}
          />
          <DatePickerInput
            label="To"
            value={to}
            onChange={d => setTo(d as Date | null)}
            valueFormat="DD/MM/YYYY"
            w={160}
          />
          <LocationSelect
            label="Location"
            value={locationId}
            onChange={setLocationId}
            placeholder="Pick a location"
          />
          <Checkbox
            label="Only changed since yesterday"
            checked={onlyChanges}
            onChange={e => setOnlyChanges(e.currentTarget.checked)}
          />
          <Button
            leftSection={<IconCopy size={16} />}
            disabled={selectedId == null}
            onClick={copy}
            ml="auto"
          >
            Copy row
          </Button>
        </Group>
      </Card>

      <Card withBorder padding={0} radius="md">
        {params == null ? (
          <Text c="dimmed" p="md">
            Pick a date range and location, then the matching visitors appear here.
          </Text>
        ) : query.isPending ? (
          <Stack gap="xs" p="md">{[1, 2, 3, 4].map(i => <Skeleton key={i} h={24} />)}</Stack>
        ) : query.isError ? (
          <Text c="red" p="md">
            Report failed — {(query.error as Error)?.message ?? 'unknown error'}
          </Text>
        ) : (
          <ScrollArea h={520} type="auto">
            <Table striped highlightOnHover stickyHeader fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 32 }} />
                  <Table.Th>First Name</Table.Th>
                  <Table.Th>Last Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Country</Table.Th>
                  <Table.Th>Nationality</Table.Th>
                  <Table.Th>Address</Table.Th>
                  <Table.Th>DOB</Table.Th>
                  <Table.Th>Passport</Table.Th>
                  <Table.Th>Valid till</Table.Th>
                  <Table.Th>Phones</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={11}>
                      <Group justify="center" py="md" gap="xs">
                        <IconReportAnalytics size={20} />
                        <Text c="dimmed" size="sm">No visitors in that range.</Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
                {rows.map(r => {
                  const isSelected = selectedId === r.id;
                  return (
                  <Table.Tr
                    key={r.id}
                    style={{ cursor: 'pointer' }}
                    bg={isSelected ? 'var(--mantine-color-blue-light)' : undefined}
                    onClick={() => setSelectedId(r.id)}
                  >
                    <Table.Td onClick={e => e.stopPropagation()}>
                      <Radio
                        name="gov-form-row"
                        checked={isSelected}
                        onChange={() => setSelectedId(r.id)}
                      />
                    </Table.Td>
                    <Table.Td>{r.firstName ?? ''}</Table.Td>
                    <Table.Td>{r.lastName ?? ''}</Table.Td>
                    <Table.Td>{r.gender ?? ''}</Table.Td>
                    <Table.Td>{r.country ?? ''}</Table.Td>
                    <Table.Td>{r.nationality ?? ''}</Table.Td>
                    <Table.Td style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.address ?? ''}
                    </Table.Td>
                    <Table.Td>{formatDmy(r.dateOfBirth ?? null)}</Table.Td>
                    <Table.Td>{r.passportNumber ?? ''}</Table.Td>
                    <Table.Td>{formatDmy(r.passportValidTill ?? null)}</Table.Td>
                    <Table.Td>{r.phones ?? ''}</Table.Td>
                  </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Stack>
  );
}

function firstOfMonth(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function lastOfMonth(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
